import type { DragState } from '../hooks/useSheetHandlers';
import { getSnapPointSheetHeight } from './sheetHeightUtils';

const VELOCITY_MULTIPLIER = 200;
const MAX_DEFAULT_REGEX = /(\d|.)+max$/;

export const MAX_SUFFIX = 'max';

/**
 * Initialise the snap points array.
 * Add 0 as a snap point to dismiss the sheet, and remove any invalid snap points
 */
export const initialiseSnapPoints = (snapPoints: number[], preventDragDismiss: boolean): number[] => {
    const points = snapPoints.filter((snapPoint) => snapPoint <= 1 && snapPoint > 0).sort();

    return preventDragDismiss ? points : [0, ...points];
};

export const getSnapPointTopValue = (snapPoint: number, topInset = 0): number =>
    window.innerHeight - getSnapPointSheetHeight(snapPoint, topInset);

export const getNewActiveSnapPoint = (dragState: DragState, snapPointsState: number[], topInset = 0): number => {
    const { currentY, sheetTouchOffset, velocity } = dragState;

    // If user is dragging over a certain velocity, adjust the intended position to be further in that direction
    // Multiplier is just an arbitrary number based on what feels good. Velocity in px/ms
    const velocityAdjustment = VELOCITY_MULTIPLIER * velocity;
    const intendedSheetTop = currentY - sheetTouchOffset - velocityAdjustment;
    const snapDistances = snapPointsState.map((snapPoint: number) =>
        Math.abs(intendedSheetTop - getSnapPointTopValue(snapPoint, topInset)),
    );
    const smallestDistance = Math.min(...snapDistances);

    // go to the closest snap point
    const index = snapDistances.indexOf(smallestDistance);
    return snapPointsState[index];
};

export const getDefaultSnapPointDetails = (
    snapPoint: number | string | undefined,
): { point?: number; suffix?: string } => {
    if (!snapPoint) return {};

    // Check if it has the max suffix
    if (typeof snapPoint === 'string' && snapPoint.match(MAX_DEFAULT_REGEX)) {
        return { point: Number(snapPoint.split(MAX_SUFFIX)[0]), suffix: MAX_SUFFIX };
    }

    if (typeof snapPoint === 'number') {
        return { point: snapPoint };
    }

    return {};
};

/**
 * The ceiling a content-measured snap point is held to, or null when the sheet does not measure its
 * content at all.
 *
 * Mirrors how `getInitialSnapPoint` resolves the same props: a plain numeric default fixes a share
 * of the screen, as an explicit snap point list does, while a `max` default only caps a height the
 * sheet still takes from its content.
 */
export const getContentSnapPointCeiling = (
    snapPoints: number[] | undefined,
    defaultSnapPoint: number | string | undefined,
): number | null => {
    if (snapPoints?.length) return null;

    const { point, suffix } = getDefaultSnapPointDetails(defaultSnapPoint);

    if (point && suffix !== MAX_SUFFIX) return null;

    return point ?? 1;
};
